import React, { useMemo } from "react";
import { Entity } from "@common/types/entity";
import { WorkspaceMember } from "@common/types/workspace";
import { ConsolidationBasisOptionsResult } from "@common/schemas/domains/accounting/consolidation.schema";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import { ENTITY_TYPE_LABELS } from "@components/settings/EntitySettings/entityTypeLabels";
import { Button } from "@design-system/Button/Button";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";
import { ConsolidationMembersCell } from "./ConsolidationMembersCell";
import { ConsolidationLedgerSelect } from "./ConsolidationLedgerSelect";
import styles from "./ConsolidationConfirmStep.module.scss";

type ConsolidationRowRole = "Parent" | "Subsidiary";

interface ConsolidationConfirmRow {
    entity: Entity;
    role: ConsolidationRowRole;
    ownership: string;
}

interface ConsolidationConfirmStepProps {
    parentEntity: Entity;
    subsidiaryEntities: Entity[];
    membersByEntityId: Map<number, WorkspaceMember[]>;
    ledgerOptionsByEntityUuid: ConsolidationBasisOptionsResult["ledgerOptionsByEntityUuid"];
    ledgerIdsByEntityUuid: Record<string, string>;
    isLoadingBasisOptions: boolean;
    basisOptionsError: string | null;
    onLedgerChange: (entityUuid: string, ledgerId: string) => void;
    onRetryBasisOptions: () => void;
    onBack: () => void;
}

export const ConsolidationConfirmStep: React.FC<
    ConsolidationConfirmStepProps
> = ({
    parentEntity,
    subsidiaryEntities,
    membersByEntityId,
    ledgerOptionsByEntityUuid,
    ledgerIdsByEntityUuid,
    isLoadingBasisOptions,
    basisOptionsError,
    onLedgerChange,
    onRetryBasisOptions,
    onBack,
}) => {
    const rows = useMemo<ConsolidationConfirmRow[]>(
        () => [
            { entity: parentEntity, role: "Parent", ownership: "-" },
            ...subsidiaryEntities.map((entity): ConsolidationConfirmRow => ({
                entity,
                role: "Subsidiary",
                ownership: "100%",
            })),
        ],
        [parentEntity, subsidiaryEntities],
    );

    if (isLoadingBasisOptions) {
        return (
            <VStack
                className={styles.state}
                gap={8}
                padding={24}
                align="center"
            >
                <strong>Checking ledger basis options…</strong>
                <span>
                    Finding the available ledger for each consolidating entity.
                </span>
            </VStack>
        );
    }

    if (basisOptionsError) {
        return (
            <VStack
                className={styles.state}
                gap={12}
                padding={24}
                align="center"
            >
                <strong>Couldn&apos;t load ledger basis options</strong>
                <span>{basisOptionsError}</span>
                <HStack gap={8}>
                    <Button variant="primary" size="md" onClick={onBack}>
                        Back to entity selection
                    </Button>
                    <Button
                        variant="tertiary"
                        size="md"
                        onClick={onRetryBasisOptions}
                    >
                        Try again
                    </Button>
                </HStack>
            </VStack>
        );
    }

    return (
        <VStack className={styles.step}>
            <div className={styles.table}>
                <div className={styles.headerRow}>
                    <span>Entity</span>
                    <span>Entity type</span>
                    <span>Ownership</span>
                    <span>Members</span>
                    <span>Role</span>
                    <span>Ledger</span>
                </div>
                {rows.map((row) => (
                    <div key={row.entity.uuid} className={styles.row}>
                        <span
                            className={
                                row.role === "Subsidiary"
                                    ? `${styles.entityLabel} ${styles.subsidiaryIndent}`
                                    : styles.entityLabel
                            }
                        >
                            <EntityAvatar
                                size="sm"
                                entity={row.entity}
                                aria-hidden
                            />
                            <span className={styles.entityName}>
                                {row.entity.name ?? "Untitled entity"}
                            </span>
                        </span>
                        <span className={styles.entityType}>
                            {row.entity.legalType
                                ? ENTITY_TYPE_LABELS[row.entity.legalType]
                                : "-"}
                        </span>
                        <span
                            className={
                                row.ownership === "-"
                                    ? styles.ownershipEmpty
                                    : styles.ownership
                            }
                        >
                            {row.ownership}
                        </span>
                        <span className={styles.membersCell}>
                            <ConsolidationMembersCell
                                members={
                                    membersByEntityId.get(row.entity.id) ?? []
                                }
                            />
                        </span>
                        <span className={styles.role}>{row.role}</span>
                        <span className={styles.basisCell}>
                            <ConsolidationLedgerSelect
                                entityUuid={row.entity.uuid}
                                ledgerOptions={
                                    ledgerOptionsByEntityUuid[
                                        row.entity.uuid
                                    ] ?? []
                                }
                                selectedLedgerId={
                                    ledgerIdsByEntityUuid[row.entity.uuid]
                                }
                                onLedgerChange={onLedgerChange}
                            />
                        </span>
                    </div>
                ))}
            </div>
        </VStack>
    );
};
